/**
 * StickyTotalsBar — Barre fixée en bas d'écran affichant totaux compacts.
 *
 * Phase 3f — Layout responsive éditeur devis (Bug 8).
 * Affichée uniquement en < qeditor (1440 px). Sur desktop ≥ 1440 px,
 * le TotalsPanel latéral est utilisé à la place. Ce seuil couvre tous les
 * iPads (11" 1194, standard 1180, 13" 1366) en layout empilé.
 *
 * Positionnement `fixed bottom-0` avec offset gauche = largeur de la sidebar
 * BackofficeLayout (`md:left-60` = 15rem = 240 px) pour ne pas la chevaucher.
 * Sur mobile (< md), sidebar cachée → `left-0`.
 *
 * Le container éditeur a `pb-24 qeditor:pb-0` pour laisser 96 px de padding-bottom
 * afin que le contenu ne soit jamais masqué par la barre.
 *
 * Contenu compact optimisé pour lecture rapide pendant le scroll :
 * - Sous-total HTVA à gauche
 * - Breakdown TVA (6% / 21% / Cocontractant selon disponibilité)
 * - Total TVAC en évidence à droite (Sora Bold, accent color, tabular-nums)
 */
import { formatCurrencyBE } from "@/lib/currency";

export default function StickyTotalsBar({ preview, accentColor, className = "" }) {
  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-30 border-t border-slate-700 bg-slate-900/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.4)] backdrop-blur md:left-60 sm:px-6 ${className}`}
      data-testid="quote-sticky-totals-bar"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-6">
        {/* Sous-total HTVA */}
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Sous-total HTVA</span>
          <span
            className="tabular text-sm font-semibold text-slate-100"
            data-testid="sticky-total-htva"
          >
            {formatCurrencyBE(preview.subtotal)}
          </span>
        </div>

        {/* Breakdown TVA — compact horizontal */}
        <div className="flex flex-wrap items-center gap-4 text-xs">
          {Object.entries(preview.byRate).map(([rate, amount]) => (
            <div key={rate} className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">TVA {rate}%</span>
              <span
                className="tabular text-slate-200"
                data-testid={`sticky-total-vat-${rate}`}
              >
                {formatCurrencyBE(amount)}
              </span>
            </div>
          ))}
          {preview.hasCocontractant && (
            <div className="flex flex-col items-end">
              <span className="text-[10px] uppercase tracking-wider text-[#C4D68A]">Autoliq.</span>
              <span className="tabular text-[#C4D68A]" data-testid="sticky-total-cocontractant">
                {formatCurrencyBE(0)}
              </span>
            </div>
          )}
        </div>

        {/* Total TVAC en évidence */}
        <div className="flex flex-col items-end">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Total TVAC</span>
          <span
            className="tabular text-xl font-bold sm:text-2xl"
            style={{ color: accentColor, fontFamily: "'Sora', sans-serif" }}
            data-testid="sticky-total-tvac"
          >
            {formatCurrencyBE(preview.tvac)}
          </span>
        </div>
      </div>
    </div>
  );
}
